import { reactive } from 'vue'
import * as api from '@/api';
import * as coreApi from '@/api/core';
import * as settingsApi from '@/api/settings';
import * as formsApi from '@/api/forms';
import * as localizationApi from '@/api/localization';
import trainingContent from '@/assets/trainingContent.json';
const IMPERSONATION_DURATION_MS = 60 * 60 * 1000; // 60 minutes
const TRAINING_COMPLETED_KEY = 'dq_training_completed';


export const store = reactive({
  accountInfo: {},
  accountInfoLastPull: 0,
  accountSettings: {},
  userSettings: {},
  isDashQuillAdmin: false,
  isDashQuillEmployee: false,
  isAccountAdmin: false,
  impersonation: { active: false, accountName: null, accountId: null, expiresAt: null, role: null },
  adminAccounts: [],
  adminRegistrations: [],
  adminSidebarCollapsed: false,
  accountFaxNumber: null,
  hideResolved: false,

  // Enabled services (cached)
  enabledServices: {},
  enabledServicesLastPull: 0,

  indicators: {}, // Refactor to messagingIndicators
  faxIndicators: {},
  submissionIndicators: {},
  telehealthIndicators: {},
  accountMessagingInfo: {}, // Account Messaging Settings
  accountServiceInfo: {}, // Account service settings

  accountUsers: [],
  accountInvitations: [],

  userInfo: {},
  userInfoLastPull: 0,
  userSettingsLastPull: 0,

  // Faxing
  faxingTags: [],

  // Messaging
  messagingTags: [],

  // Forms
  formsTags: [],
  accountForms: [],
  accountFormsLastPull: 0,
  accountPackets: [],
  accountPacketsLastPull: 0,
  ehrFields: [],
  ehrFieldQuery: '',

  // Telehealth
  ehrEncounterSections: [],
  
  // Voice
  incomingCalls: [],
  activeCalls: [],
  voicePhoneInfo: {},
  phoneIndicators: {},
  phoneActiveCall: null,
  phoneIsMuted: false,

  // Tasks
  loadedTasks: [],
  taskTags: [],
  taskReturnPath: null,

  // EHR embed auth error (shown as modal on apps page)
  ehrAuthError: '',

  // Messaging Templates
  messageTemplates: [],
  messageTemplatesLastPull: 0,

  // Billing alert
  billingAlert: { status: null, startDate: null },

  // Background AI tasks
  backgroundTasks: [],

  // Service onboarding
  serviceOnboarding: {},
  serviceOnboardingLastPull: 0,

  // Training progress (watched videos and extension installs) lives only in this browser; the video
  // manifest is assets/trainingContent.json and the account's EHR + services come from accountSettings
  trainingCompletedSteps: []
})

// Initialize hideResolved from localStorage
try {
  const saved = localStorage.getItem('dq_hide_resolved');
  if (saved !== null) store.hideResolved = saved === 'true';
} catch { /* ignore */ }

// Initialize training progress from localStorage (watched video URLs and extension installs);
// it only lives in this browser, so losing it just clears the checkmarks
try {
  const saved = JSON.parse(localStorage.getItem(TRAINING_COMPLETED_KEY));
  if (Array.isArray(saved)) store.trainingCompletedSteps = saved.filter(id => typeof id === 'string');
} catch { /* ignore */ }

export const fetchAccountInfo = async (timeToIgnore = 5000) => {
  if (Date.now() < store.accountInfoLastPull + timeToIgnore) {
    console.debug('Did not fetch user info because we already have a fresh copy');
    return;
  }

  try {
    const account = await api.fetchAccountInfo();
    store.accountInfo = account;
    store.accountInfoLastPull = Date.now();
  } catch (err) {
    throw Error('Failed to fetch account info', err);
  }
}

export const fetchUserInfo = async (timeToIgnore = 5000) => {
  if (Date.now() < store.userInfoLastPull + timeToIgnore) {
    console.debug('Did not fetch user info because we already have a fresh copy');
    return;
  }

  try {
    const user = await api.userInfo();
    store.userInfo = user;
    store.userInfoLastPull = Date.now();
  } catch (err) {
    throw Error('Failed to fetch user info', err);
  }
}

export const fetchUserSettings = async (timeToIgnore = 5000) => {
  if (Date.now() < store.userSettingsLastPull + timeToIgnore) {
    console.debug('Did not fetch user settings because we already have a fresh copy');
    return;
  }

  try {
    const settings = await coreApi.getUserSettings();
    store.userSettings = settings;
    store.userSettingsLastPull = Date.now();
  } catch (err) {
    console.error('Failed to fetch user settings', err);
  }
}

export const fetchEnabledServices = async (timeToIgnore = 5000) => {
  if (Date.now() < store.enabledServicesLastPull + timeToIgnore) {
    console.debug('Did not fetch enabled services because we already have a fresh copy');
    return;
  }

  try {
    const services = await settingsApi.getEnabledServices();
    store.enabledServices = services;
    store.enabledServicesLastPull = Date.now();
  } catch (err) {
    throw Error('Failed to fetch enabled services', err);
  }
}

export const fetchMessageTemplates = async (timeToIgnore = 5000) => {
  if (Date.now() < store.messageTemplatesLastPull + timeToIgnore) {
    console.debug('Did not fetch message templates because we already have a fresh copy');
    return;
  }

  try {
    const templates = await api.fetchTemplates();
    store.messageTemplates = templates;
    store.messageTemplatesLastPull = Date.now();
  } catch (err) {
    console.error('Failed to fetch message templates', err);
  }
}

export const fetchAccountForms = async (timeToIgnore = 5000) => {
  if (Date.now() < store.accountFormsLastPull + timeToIgnore) {
    return;
  }

  try {
    const forms = await formsApi.listForms();
    store.accountForms = forms;
    store.accountFormsLastPull = Date.now();
  } catch (err) {
    console.error('Failed to fetch account forms', err);
  }
}

export const fetchAccountPackets = async (timeToIgnore = 5000) => {
  if (Date.now() < store.accountPacketsLastPull + timeToIgnore) {
    return;
  }

  try {
    const packets = await formsApi.listPackets();
    store.accountPackets = packets;
    store.accountPacketsLastPull = Date.now();
  } catch (err) {
    console.error('Failed to fetch account packets', err);
  }
}

// Impersonation helpers

function resetAccountState() {
  store.accountSettings = {}
  store.accountInfo = {}
  store.accountInfoLastPull = 0
  store.accountMessagingInfo = {}
  store.accountServiceInfo = {}
  store.accountUsers = []
  store.accountInvitations = []
  store.userInfo = {}
  store.userInfoLastPull = 0
  store.userSettings = {}
  store.userSettingsLastPull = 0
  store.enabledServices = {}
  store.enabledServicesLastPull = 0
  store.faxingTags = []
  store.messagingTags = []
  store.formsTags = []
  store.accountForms = []
  store.accountFormsLastPull = 0
  store.accountPackets = []
  store.accountPacketsLastPull = 0
  store.serviceOnboarding = {}
  store.serviceOnboardingLastPull = 0
  store.billingAlert = { status: null, startDate: null }
}

export const attemptImpersonationLogin = (accountId, accountName, router) => {
  resetAccountState()

  const expiresAt = Date.now() + IMPERSONATION_DURATION_MS
  const role = store.isDashQuillAdmin ? 'admin' : 'employee'

  // Store in localStorage — this is the single source of truth
  localStorage.setItem('dq_impersonation', JSON.stringify({ accountId, accountName, expiresAt, role }))

  store.impersonation = { active: true, accountName, accountId, expiresAt, role }
  router.push('/apps')
}

export const loadImpersonation = () => {
  try {
    const raw = localStorage.getItem('dq_impersonation');
    if (!raw) return;
    const data = JSON.parse(raw);
    if (!data.accountId || !data.expiresAt || data.expiresAt <= Date.now()) {
      localStorage.removeItem('dq_impersonation');
      return;
    }
    store.impersonation = {
      active: true,
      accountName: data.accountName,
      accountId: data.accountId,
      expiresAt: data.expiresAt,
      role: data.role
    };
  } catch {
    localStorage.removeItem('dq_impersonation');
  }
}

export const clearImpersonation = () => {
  localStorage.removeItem('dq_impersonation');
  resetAccountState();
  store.impersonation = { active: false, accountName: null, accountId: null, expiresAt: null, role: null };
}

// Billing alert helpers

export const updateBillingAlert = (settings) => {
  store.billingAlert = {
    status: settings.billingAlertStatus || null,
    startDate: settings.billingAlertStartDate || null
  }
}

// Background AI task helpers

export const startBackgroundAiTranslate = (key, language, fields) => {
  return localizationApi.aiTranslate(key, language, fields)
}

// Service onboarding helpers

export const fetchServiceOnboarding = async (timeToIgnore = 5000) => {
  if (Date.now() < store.serviceOnboardingLastPull + timeToIgnore) {
    return;
  }

  try {
    const result = await settingsApi.getServiceOnboardingStatus();
    store.serviceOnboarding = result;
    store.serviceOnboardingLastPull = Date.now();
  } catch (err) {
    console.error('Failed to fetch service onboarding status', err);
  }
}

// Training helpers

// Each service's training page and the account setting that turns the service on
const TRAINING_SERVICES = {
  forms: { name: 'Forms Training', enabledSetting: 'isFormsEnabled' },
  messaging: { name: 'Messaging Training', enabledSetting: 'isMessagingEnabled' },
  faxing: { name: 'Faxing Training', enabledSetting: 'isFaxingEnabled' },
  telehealth: { name: 'Telehealth Training', enabledSetting: 'isTelehealthEnabled' }
}

// A manifest video's `section`: setup videos are titled "For Admins" and listed after the usage tutorials;
// a video without one is a usage tutorial
export const TRAINING_VIDEO_SECTION = Object.freeze({
  USAGE: 'usage',
  SETUP: 'setup'
})

/**
 * A service's training videos: the usage tutorials (the account EHR's walkthroughs, then the EHR-agnostic
 * platform videos), then the setup videos for admins
 */
export const getServiceTrainingVideos = (service) => {
  const emrVideos = trainingContent.emr[store.accountSettings.emr]?.videos?.[service] || []
  const platformVideos = trainingContent.platformVideos?.[service] || []
  const videos = [...emrVideos, ...platformVideos]
  const setupVideos = videos.filter(video => video.section === TRAINING_VIDEO_SECTION.SETUP)
  return [...videos.filter(video => video.section !== TRAINING_VIDEO_SECTION.SETUP), ...setupVideos]
}

/** The extension install step is kept per EHR: it completes when the installed extension matches the account's EHR */
export const getExtensionInstallStepId = () => `install_extension:${store.accountSettings.emr}`

/**
 * Each training page with how many of its steps are done in this browser: the extension page (install video +
 * install step), then every enabled service that has videos. A video's step id is its URL.
 */
export const getTrainingSections = () => {
  // Nothing to list until the account settings arrive (they always carry `emr`)
  if (!store.accountSettings.emr) return []
  const installVideo = trainingContent.emr[store.accountSettings.emr]?.installVideo
  const extensionSteps = installVideo ? [installVideo.url, getExtensionInstallStepId()] : [getExtensionInstallStepId()]
  const sections = [{ path: '/training/extension', name: 'Extension', steps: extensionSteps }]
  for (const [service, { name, enabledSetting }] of Object.entries(TRAINING_SERVICES)) {
    const steps = getServiceTrainingVideos(service).map(video => video.url)
    if (store.accountSettings[enabledSetting] && steps.length > 0) {
      sections.push({ path: `/training/${service}`, name, steps })
    }
  }
  return sections.map(({ path, name, steps }) => {
    const completed = steps.filter(id => store.trainingCompletedSteps.includes(id)).length
    return { path, name, total: steps.length, completed, complete: completed === steps.length }
  })
}

export const setTrainingStepComplete = (stepId, complete) => {
  if (typeof stepId !== 'string' || !stepId) throw Error('Training step id must be a non-empty string')
  const completedSteps = store.trainingCompletedSteps.filter(id => id !== stepId)
  if (complete) completedSteps.push(stepId)
  store.trainingCompletedSteps = completedSteps
  try {
    localStorage.setItem(TRAINING_COMPLETED_KEY, JSON.stringify(completedSteps))
  } catch (err) {
    console.error('Failed to save training progress', err)
  }
}